import apiCall, { endpoints } from './api';
import { profileService } from './profileService';

/**
 * Service d'authentification enrichi avec gestion du profil
 */
export const authService = {
  /**
   * ✅ Connexion utilisateur
   */
  login: async (credentials) => {
    try {
      
      const response = await apiCall.post(endpoints.auth.login, credentials);

      if (response.success && response.data?.token) {
        const loginData = response.data;
        
        // ✅ CORRECTION: Le backend envoie des champs séparés, pas un objet user
        // Construire l'objet user à partir des champs de LoginResponse
        const user = {
          id: loginData.userId || loginData.user_id,
          username: loginData.username,
          role: loginData.role,
          boutiqueId: loginData.boutiqueId || loginData.boutique_id,
          // Ces champs seront récupérés via /auth/profile si nécessaire
          firstName: '',
          lastName: '',
          email: credentials.username // Temporaire, sera mis à jour par getProfile
        };
        
        // Stocker le token et les informations utilisateur
        localStorage.setItem('token', loginData.token);
        localStorage.setItem('userInfo', JSON.stringify(user));
        
        
        return {
          success: true,
          data: { 
            token: loginData.token, 
            user: user 
          },
          message: response.message || 'Connexion réussie'
        };
      } else {
        throw new Error(response.message || 'Identifiants invalides: Veuillez verifier votre username ou mot de passe!');
      }
    } catch (error) {
      console.error('❌ Erreur de connexion:', error);
      
      const status = error.response?.status;
      const backendMessage = error.response?.data?.message;

      return {
        success: false,
        message: backendMessage
          || (status === 401 ? 'Identifiants incorrects. Vérifiez votre email/téléphone et votre mot de passe.' : null)
          || error.message
          || 'Erreur de connexion'
      };
    }
  },

  /**
   * ✅ Déconnexion utilisateur
   */
  logout: async () => {
    try {
      // Appeler l'endpoint de déconnexion si disponible
      try {
        await apiCall.post(endpoints.auth.logout);
      } catch (error) {
        console.warn('⚠️ Erreur déconnexion côté serveur (ignorée):', error.message);
      }
    } finally {
      // Nettoyer le stockage local
      localStorage.removeItem('token');
      localStorage.removeItem('userInfo');
      localStorage.removeItem('selectedBoutique');
    }
  },

  /**
   * ✅ Récupérer le profil utilisateur
   */
  getProfile: async () => {
    return await profileService.getProfile();
  },

  /**
   * ✅ Mettre à jour le profil utilisateur
   */
  updateProfile: async (profileData) => {
    return await profileService.updateProfile(profileData);
  },

  /**
   * ✅ Changer le mot de passe
   */
  changePassword: async (passwordData) => {
    try {
      
      // Validation des données
      if (!passwordData.currentPassword || !passwordData.newPassword) {
        throw new Error('Mot de passe actuel et nouveau mot de passe requis');
      }

      if (passwordData.newPassword.length < 6) {
        throw new Error('Le nouveau mot de passe doit contenir au moins 6 caractères');
      }

      const response = await apiCall.post(endpoints.auth.changePassword, {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      
      
      if (response.success) {
        return response;
      } else {
        throw new Error(response.message || 'Erreur lors du changement de mot de passe');
      }
      
    } catch (error) {
      console.error('❌ Erreur changement mot de passe:', error);
      
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Erreur lors du changement de mot de passe'
      };
    }
  },

  /**
   * ✅ Vérifier la validité du token
   */
  verifyToken: async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        return false;
      }
      
      // Vérifier côté client d'abord (expiration)
      if (isTokenExpired(token)) {
        return false;
      }
      
      // Vérifier côté serveur
      const response = await apiCall.get(endpoints.auth.verify);
      return response.success && response.data === true;
      
    } catch (error) {
      console.error('❌ Erreur vérification token:', error);
      return false;
    }
  },

  /**
   * ✅ Rafraîchir le token
   */
  refreshToken: async () => {
    try {
      const response = await apiCall.post(endpoints.auth.refresh);
      
      if (response.success && response.data?.token) {
        localStorage.setItem('token', response.data.token);
        return response;
      } else {
        throw new Error('Impossible de rafraîchir le token');
      }
      
    } catch (error) {
      console.error('❌ Erreur rafraîchissement token:', error);
      return { success: false, message: error.message };
    }
  },

  /**
   * ✅ Obtenir les informations utilisateur depuis le cache
   */
  getCachedUser: () => {
    try {
      const userInfo = localStorage.getItem('userInfo');
      return userInfo ? JSON.parse(userInfo) : null;
    } catch (error) {
      console.error('❌ Erreur lecture cache utilisateur:', error);
      return null;
    }
  },

  /**
   * ✅ Vérifier si l'utilisateur est connecté
   */
  isAuthenticated: () => {
    const token = localStorage.getItem('token');
    const user = authService.getCachedUser();
    return !!(token && user);
  },

  /**
   * ✅ Vérifier si l'utilisateur a un rôle spécifique
   */
  hasRole: (roles) => {
    const user = authService.getCachedUser();
    if (!user) return false;

    if (Array.isArray(roles)) {
      return roles.includes(user.role);
    }
    return user.role === roles;
  },

  /**
   * ✅ Vérifier si l'utilisateur peut accéder à une boutique
   */
  canAccessBoutique: (boutiqueId) => {
    const user = authService.getCachedUser();
    if (!user) return false;

    // Super admin peut accéder à toutes les boutiques
    if (user.role === 'SUPER_ADMIN') return true;

    // Manager et Employee : seulement leur boutique
    return user.boutiqueId === boutiqueId || user.boutique?.id === boutiqueId;
  },

  /**
   * ✅ NOUVEAU: Récupérer l'historique des connexions
   */
  getLoginHistory: async () => {
    return await profileService.getLoginHistory();
  },

  /**
   * ✅ NOUVEAU: Obtenir des statistiques du profil
   */
  getProfileStats: () => {
    const user = authService.getCachedUser();
    if (!user) return null;

    return {
      joinDate: user.createdAt,
      lastLogin: user.lastLogin,
      loginCount: user.loginCount || 0,
      profileCompletion: calculateProfileCompletion(user)
    };
  }
};

/**
 * ✅ Calculer le pourcentage de completion du profil
 */
function calculateProfileCompletion(user) {
  if (!user) return 0;

  const fields = [
    'firstName',
    'lastName', 
    'email',
    'phone',
    'username'
  ];

  const completedFields = fields.filter(field => user[field] && user[field].trim());
  const completion = Math.round((completedFields.length / fields.length) * 100);
  
  return completion;
}

/**
 * ✅ Décoder un token JWT
 */
function decodeJWT(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('❌ Erreur décodage JWT:', error);
    return null;
  }
}

/**
 * ✅ Vérifier si un token JWT est expiré
 */
function isTokenExpired(token) {
  try {
    const decoded = decodeJWT(token);
    if (!decoded || !decoded.exp) return true;

    const currentTime = Date.now() / 1000;
    return decoded.exp < currentTime;
  } catch (error) {
    console.error('❌ Erreur vérification expiration:', error);
    return true;
  }
}

/**
 * ✅ Obtenir le temps restant avant expiration du token
 */
export function getTokenTimeRemaining(token) {
  try {
    const decoded = decodeJWT(token);
    if (!decoded || !decoded.exp) return 0;

    const currentTime = Date.now() / 1000;
    const timeRemaining = decoded.exp - currentTime;
    return Math.max(0, timeRemaining);
  } catch (error) {
    return 0;
  }
}

/**
 * ✅ Formater le nom d'utilisateur pour l'affichage
 */
export function formatUserName(user) {
  if (!user) return 'Utilisateur';
  
  const firstName = user.firstName || '';
  const lastName = user.lastName || '';
  
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }
  
  return firstName || lastName || user.username || user.email || 'Utilisateur';
}

/**
 * ✅ Obtenir l'icône correspondant au rôle
 */
export function getRoleIcon(role) {
  switch (role) {
    case 'SUPER_ADMIN': return '👑';
    case 'MANAGER': return '🏪';
    case 'EMPLOYEE': return '👤';
    default: return '🔒';
  }
}

/**
 * ✅ Obtenir le label du rôle en français
 */
export function getRoleLabel(role) {
  switch (role) {
    case 'SUPER_ADMIN': return 'Super Administrateur';
    case 'MANAGER': return 'Gestionnaire';
    case 'EMPLOYEE': return 'Employé';
    default: return 'Utilisateur';
  }
}

export default authService;